// Durées SCORM : ISO 8601 (2004) et CMITimespan (1.2), bornées contre les horloges folles.

/**
 * Plafond de temps cumulé raisonnable (1 an). Au-delà, on considère que le
 * module/driver SCORM a envoyé une durée aberrante (ex. horloge système fausse)
 * et on ignore la valeur plutôt que de stocker un temps impossible.
 */
const MAX_TOTAL_TIME_MS = 366 * 24 * 60 * 60 * 1000

/** Parse an ISO 8601 duration (e.g. PT1H30M45.5S) to milliseconds */
export function iso8601DurationToMs(duration: string): number {
  const match = duration.match(/^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/)
  if (!match) return 0
  const hours = Number.parseInt(match[4] || '0', 10)
  const minutes = Number.parseInt(match[5] || '0', 10)
  const seconds = Number.parseFloat(match[6] || '0')
  const ms = ((hours * 3600) + (minutes * 60) + seconds) * 1000
  // Borne les durées aberrantes (horloge système fausse côté contenu SCORM).
  if (!Number.isFinite(ms) || ms < 0 || ms > MAX_TOTAL_TIME_MS) return 0
  return ms
}

/** Convert milliseconds to ISO 8601 duration (PT#H#M#.##S) */
export function msToIso8601Duration(ms: number): string {
  const totalSeconds = Math.round(ms / 10) / 100 // round to centiseconds
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Number.parseFloat((totalSeconds % 60).toFixed(2))
  return `PT${hours}H${minutes}M${seconds}S`
}

/**
 * Parse a SCORM 1.2 CMITimespan (HHHH:MM:SS[.SS]) to milliseconds.
 * Renvoie null si le format est invalide ou la durée aberrante.
 */
export function cmiTimespanToMs(timespan: string): number | null {
  const match = timespan.match(/^(\d{2,4}):(\d{2}):(\d{2}(?:\.\d{1,2})?)$/)
  if (!match) return null
  const hours = Number.parseInt(match[1], 10)
  const minutes = Number.parseInt(match[2], 10)
  const seconds = Number.parseFloat(match[3])
  const ms = ((hours * 3600) + (minutes * 60) + seconds) * 1000
  if (!Number.isFinite(ms) || ms < 0 || ms > MAX_TOTAL_TIME_MS) return null
  return ms
}

/** Convert milliseconds to a SCORM 1.2 CMITimespan (HHHH:MM:SS[.SS]) */
export function msToCmiTimespan(ms: number): string {
  const totalCentis = Math.round(ms / 10)
  const centis = totalCentis % 100
  const totalSeconds = Math.floor(totalCentis / 100)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const base = `${String(hours).padStart(4, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return centis > 0 ? `${base}.${String(centis).padStart(2, '0')}` : base
}
